/**
 * Hook générique pour créer un parcours d'évaluation
 *
 * Ce hook remplace les 3 hooks codés en dur précédents en acceptant
 * une configuration de parcours dynamique.
 */

import { useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import {
  TypeEvalEnum,
  StatutGlobalEnum,
  StatutDetailInbasketEnum,
  TemplateFormEnum,
  TemplateInbasketEnum,
  TemplateEntStructEnum,
} from "../../../../convex/schema";
import type { ParcoursTypeConfig, EvalConfig } from "./parcoursConfig";

// ============================================
// TYPES
// ============================================

export interface CandidatInfo {
  emailCandidat: string;
  nomCandidat: string;
  prenomCandidat: string;
  token?: string;
  anonyme?: boolean;
}

export interface CreerParcoursResult {
  parcoursId: Id<"parcoursEvaluations">;
  evaluationsIds: Id<"evaluations">[];
}

// Type union pour les données d'évaluation
type InbasketDonnees = {
  type: typeof TypeEvalEnum.INBASKET;
  template?: TemplateInbasketEnum;
  statutDetail: typeof StatutDetailInbasketEnum.NON_COMMENCEE;
  statutsDocuments: Record<string, never>;
  reponses: Record<string, never>;
};

type FormDonnees = {
  type: typeof TypeEvalEnum.FORM;
  template: TemplateFormEnum;
  reponses: Record<string, never>;
};

type EntStructDonnees = {
  type: typeof TypeEvalEnum.ENT_STRUCT;
  template?: TemplateEntStructEnum;
  reponses: Record<string, never>;
};

type EvaluationDonnees = InbasketDonnees | FormDonnees | EntStructDonnees;

// ============================================
// HELPERS
// ============================================

/**
 * Construit les données initiales pour une évaluation Inbasket
 */
function buildInbasketDonnees(
  template?: TemplateInbasketEnum,
): InbasketDonnees {
  return {
    type: TypeEvalEnum.INBASKET,
    template,
    statutDetail: StatutDetailInbasketEnum.NON_COMMENCEE,
    statutsDocuments: {},
    reponses: {},
  };
}

/**
 * Construit les données initiales pour une évaluation Form
 */
function buildFormDonnees(template: TemplateFormEnum): FormDonnees {
  return {
    type: TypeEvalEnum.FORM,
    template,
    reponses: {},
  };
}

/**
 * Construit les données initiales pour une évaluation Ent-struct
 */
function buildEntStructDonnees(
  template?: TemplateEntStructEnum,
): EntStructDonnees {
  return {
    type: TypeEvalEnum.ENT_STRUCT,
    template,
    reponses: {},
  };
}

/**
 * Construit les données initiales pour une évaluation selon son type et template
 */
function buildDonnees(evalConfig: EvalConfig): EvaluationDonnees {
  switch (evalConfig.typeEval) {
    case TypeEvalEnum.INBASKET:
      return buildInbasketDonnees(
        evalConfig.template as TemplateInbasketEnum | undefined,
      );
    case TypeEvalEnum.FORM:
      return buildFormDonnees(evalConfig.template as TemplateFormEnum);
    case TypeEvalEnum.ENT_STRUCT:
      return buildEntStructDonnees(
        evalConfig.template as TemplateEntStructEnum | undefined,
      );
    default:
      throw new Error(`Type d'évaluation non supporté: ${evalConfig.typeEval}`);
  }
}

// ============================================
// HOOK PRINCIPAL
// ============================================

/**
 * Hook React pour créer un parcours d'évaluation avec une configuration dynamique
 *
 * @returns Une fonction qui crée le parcours et retourne l'ID du parcours créé
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const creerParcours = useCreerParcours();
 *
 *   const handleCreate = async () => {
 *     const result = await creerParcours(
 *       {
 *         emailCandidat: "john@example.com",
 *         nomCandidat: "Doe",
 *         prenomCandidat: "John",
 *       },
 *       PARCOURS_TYPES.evaluation_manageriale,
 *     );
 *     console.log("Parcours créé:", result.parcoursId);
 *   };
 *
 *   return <button onClick={handleCreate}>Créer le parcours</button>;
 * }
 * ```
 */
export function useCreerParcours() {
  const creerEvaluation = useMutation(api.evaluations.creerEvaluation);
  const creerParcoursMutation = useMutation(
    api.parcoursEvaluations.creerParcours,
  );

  return async (
    candidat: CandidatInfo,
    config: ParcoursTypeConfig,
  ): Promise<CreerParcoursResult> => {
    const evaluationsIds: Id<"evaluations">[] = [];

    // Créer chaque évaluation selon la configuration
    for (const evalConfig of config.evaluations) {
      const evaluationId = await creerEvaluation({
        typeEval: evalConfig.typeEval,
        statut: StatutGlobalEnum.NON_COMMENCE,
        template: evalConfig.template,
        donnees: buildDonnees(evalConfig),
        skipAutoInit: true,
      });
      evaluationsIds.push(evaluationId);
    }

    // Créer le parcours avec les IDs des évaluations
    const parcoursId = await creerParcoursMutation({
      emailCandidat: candidat.emailCandidat,
      nomCandidat: candidat.nomCandidat,
      prenomCandidat: candidat.prenomCandidat,
      typeParcours: config.key,
      slug: config.key,
      titre: config.label,
      description: config.description,
      evaluationsIds,
      token: candidat.token,
      anonyme: candidat.anonyme,
    });

    return {
      parcoursId,
      evaluationsIds,
    };
  };
}
